import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { navigation, type NavigationEntry } from '@/config/navigation';
import { VIEWS } from '@/config/views';
import { useCanSee } from '@/shared/hooks/usePermission';
import { cn } from '@/shared/lib/cn';

import { BrandMark } from './BrandMark';

/**
 * Each item asks about itself rather than the parent filtering the list. That
 * is what keeps `useCanSee` a hook call — and it means a view whose gate
 * changes needs no edit here.
 */
function NavItem({ entry }: { entry: NavigationEntry }) {
  const { t } = useTranslation();
  const view = VIEWS[entry.view];
  const canSee = useCanSee(entry.view);

  if (!canSee || !view.labelKey) return null;

  return (
    <NavLink
      to={view.path}
      end
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-(--radius-control) px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-brand-900 font-medium text-white'
            : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
        )
      }
    >
      {entry.icon}
      {t(view.labelKey)}
    </NavLink>
  );
}

export function Sidebar() {
  return (
    <aside className="bg-surface border-border hidden w-60 shrink-0 border-r p-3 md:block">
      <BrandMark className="mb-6" />
      <nav className="flex flex-col gap-0.5">
        {navigation.map((entry) => (
          <NavItem key={entry.view} entry={entry} />
        ))}
      </nav>
    </aside>
  );
}
